import type {
  BeneficiarioOption,
  PendingReposicionPush,
  PendingReposicionRow,
  ReposicionExportRow,
  ReposicionListItem,
  SaveReposicionInput,
} from '../domain/models/ReposicionCatalog.ts';
import type {
  ReposicionCatalogRepositoryPort,
  ReposicionMediaPort,
  ReposicionRepositoryPort,
  ReposicionSyncPushPort,
} from '../domain/ports/ReposicionPorts.ts';
import type { PendingSyncRecord } from '@src/modules/kernel/PendingSyncRecord.ts';
import { searchBeneficiariosForFeature } from '@src/modules/platform/catalog/application/featureBeneficiarioSearch.ts';
import type { GetSqlJsDatabase } from '@src/modules/platform/local-sqlite/domain/GetSqlJsDatabase.ts';
import type { SyncPushStoredMediaRef } from '@core/domain/sync/SyncPushResult.ts';
import { mediaBase64FromId } from '@src/modules/platform/local-media/application/mediaBase64.ts';
import { runSyncPushLoop } from '@src/modules/platform/sync/application/runSyncPushLoop.ts';
import { countReposicionMediaPurgeSkipped, reposicionSyncMediaAckIssues, reposicionSyncPushPayloadIssues } from '../domain/reposicionSyncMediaPolicy.ts';
import type { CombinedSyncPushSection } from '@src/modules/platform/sync/application/runCombinedSyncPushLoop.ts';
import { checkReposicionDeliveryReadyFromPending } from './reposicionDeliveryReadinessUseCases.ts';

export type PreparedReposicionExportRecords = {
  payload: PendingReposicionPush[];
  purgeIds: string[];
  retained: number;
};

export type ReposicionPushActor = {
  usuarioPublicId: string;
};

export class ReposicionApplicationService {
  private lastMediaPurgeSkipped = 0;
  private lastExportMediaPurged = 0;
  private lastExportMediaRetained = 0;

  constructor(
    public readonly catalog: ReposicionCatalogRepositoryPort,
    public readonly reposiciones: ReposicionRepositoryPort,
    public readonly media: ReposicionMediaPort,
    private readonly pushPort: ReposicionSyncPushPort | null,
    private readonly flushPersist?: () => Promise<void>,
    private readonly getDb?: GetSqlJsDatabase,
    private readonly getToken?: () => string | null,
    private readonly markDirty?: () => void,
    private readonly resolvePushActor?: () => Promise<ReposicionPushActor | null>,
  ) {}

  async searchBeneficiariosByDocumento(query: string, itemPublicId?: string): Promise<BeneficiarioOption[]> {
    return searchBeneficiariosForFeature(
      query,
      (q) => this.catalog.searchBeneficiariosByDocumento(q, itemPublicId),
      {
        getDb: this.getDb,
        getToken: this.getToken,
        markDirty: this.markDirty,
      },
    );
  }

  async flushLocalChanges(): Promise<void> {
    if (this.flushPersist) await this.flushPersist();
  }

  async listUnsyncedSyncRows(): Promise<PendingSyncRecord[]> {
    // ponytail: no media sweep on open /sync (Safari OOM); push path validates + clears.
    const rows = await this.reposiciones.listUnsynced();
    return rows.map((row) => ({
      publicId: row.publicId,
      fecha: row.fecha,
      syncStatus: row.syncStatus,
      syncError: row.syncError,
      headline: row.beneficiarioNombre,
      subline: `${row.comunaNombre} · ${row.direccion}`,
      detail: row.productosLabel,
      medioNombre: row.medioNombre,
      motivoNombre: row.motivoNombre,
    }));
  }

  async saveReposicion(input: SaveReposicionInput): Promise<{ publicId: string }> {
    return this.reposiciones.save(input);
  }

  async updateReposicion(publicId: string, input: SaveReposicionInput): Promise<boolean> {
    return this.reposiciones.update(publicId, input);
  }

  async patchReposicionDeliveryMedia(
    publicId: string,
    patch: { firmaMediaId?: string; fotoMediaId?: string },
  ): Promise<boolean> {
    return this.reposiciones.patchReposicionDeliveryMedia(publicId, patch);
  }

  takeLastMediaPurgeSkipped(): number {
    const n = this.lastMediaPurgeSkipped;
    this.lastMediaPurgeSkipped = 0;
    return n;
  }

  takeLastExportMediaPurged(): number {
    const n = this.lastExportMediaPurged;
    this.lastExportMediaPurged = 0;
    return n;
  }

  takeLastExportMediaRetained(): number {
    const n = this.lastExportMediaRetained;
    this.lastExportMediaRetained = 0;
    return n;
  }

  async listObservabilityRows(limit = 500): Promise<ReposicionListItem[]> {
    await this.refreshAllLocalMediaValidation(limit);
    return this.reposiciones.listReposiciones(limit);
  }

  private async refreshAllLocalMediaValidation(limit = 500): Promise<void> {
    if (this.flushPersist) await this.flushPersist();
    const rows = await this.reposiciones.listAllForExport(limit);
    for (const row of rows) {
      try {
        const check = await checkReposicionDeliveryReadyFromPending(this, row);
        if (!check.ok) {
          await this.reposiciones.markFailed(row.publicId, check.message);
          continue;
        }
        if (check.warning) {
          await this.reposiciones.setSyncWarning(row.publicId, check.warning);
          continue;
        }
        if (row.syncStatus === 'failed' || row.syncError) {
          await this.reposiciones.clearSyncError(row.publicId);
        }
      } catch {
        /* one bad row must not block validating the rest */
      }
    }
  }

  async prepareExportRecordsWithMedia(): Promise<PreparedReposicionExportRecords> {
    if (this.flushPersist) await this.flushPersist();
    const rows = await this.reposiciones.listAllForExport();
    if (rows.length === 0) return { payload: [], purgeIds: [], retained: 0 };
    const payload = await this.enrichPendingPush(rows);
    const { purged, retained } = this.collectBackedUpExportMediaIds(rows, payload);
    return { payload, purgeIds: purged, retained };
  }

  async commitPreparedExportRecords(prepared: PreparedReposicionExportRecords): Promise<void> {
    if (prepared.purgeIds.length === 0) {
      this.lastExportMediaPurged = 0;
      this.lastExportMediaRetained = prepared.retained;
      return;
    }
    await Promise.all(prepared.purgeIds.map((id) => this.media.delete(id)));
    this.lastExportMediaPurged = prepared.purgeIds.length;
    this.lastExportMediaRetained = prepared.retained;
  }

  async exportRecordsWithMedia(): Promise<PendingReposicionPush[]> {
    const prepared = await this.prepareExportRecordsWithMedia();
    if (prepared.payload.length === 0) return [];
    await this.commitPreparedExportRecords(prepared);
    return prepared.payload;
  }

  combinedSyncPushSection(): CombinedSyncPushSection<PendingReposicionRow, PendingReposicionPush> {
    return {
      listPending: () => this.listPendingPushReady(),
      beforePush: (pending) => this.preparePendingPush(pending),
      markSynced: (ids) => this.reposiciones.markSynced(ids),
      markFailed: (id, msg) => this.reposiciones.markFailed(id, msg),
      afterAccepted: async (pending, ids, result) => {
        return this.afterReposicionPushAccepted(pending, ids, result.storedMedia ?? []);
      },
      publicId: (row) => row.publicId,
    };
  }

  async pushPending(): Promise<number> {
    const accepted = await runSyncPushLoop({
      pushPort: this.pushPort,
      listPending: () => this.listPendingPushReady(),
      markSynced: (ids) => this.reposiciones.markSynced(ids),
      markFailed: (id, msg) => this.reposiciones.markFailed(id, msg),
      flushPersist: this.flushPersist,
      beforePush: (pending) => this.preparePendingPush(pending),
      afterAccepted: async (pending, ids, result) => {
        return this.afterReposicionPushAccepted(pending, ids, result.storedMedia ?? []);
      },
    });
    return accepted.length;
  }

  private async listPendingPushReady(): Promise<PendingReposicionRow[]> {
    const pending = await this.reposiciones.listPendingPush();
    const ready: PendingReposicionRow[] = [];
    for (const row of pending) {
      const check = await checkReposicionDeliveryReadyFromPending(this, row);
      if (check.ok) {
        await this.reposiciones.clearSyncError(row.publicId);
        ready.push(row);
        continue;
      }
      await this.reposiciones.markFailed(row.publicId, check.message);
    }
    return ready;
  }

  private async preparePendingPush(pending: PendingReposicionRow[]): Promise<PendingReposicionPush[]> {
    if (pending.length === 0) return [];
    const payload = await this.enrichPendingPush(pending);
    // ponytail: usuario check only when resolvePushActor wired (prod).
    const issues = this.resolvePushActor ? reposicionSyncPushPayloadIssues(pending, payload) : [];
    if (issues.length === 0) return payload;
    for (const issue of issues) {
      await this.reposiciones.markFailed(issue.reposicionPublicId, issue.message);
    }
    const blocked = new Set(issues.map((issue) => issue.reposicionPublicId));
    return payload.filter((row) => !blocked.has(row.publicId));
  }

  private async afterReposicionPushAccepted(
    pending: PendingReposicionRow[],
    acceptedPublicIds: string[],
    storedMedia: SyncPushStoredMediaRef[],
  ): Promise<string[]> {
    const ackIssues = reposicionSyncMediaAckIssues(pending, acceptedPublicIds, storedMedia);
    for (const issue of ackIssues) {
      await this.reposiciones.markFailed(issue.reposicionPublicId, issue.message);
    }
    const failedByAck = new Set(ackIssues.map((issue) => issue.reposicionPublicId));
    const syncedIds = acceptedPublicIds.filter((id) => !failedByAck.has(id));
    // Defer IndexedDB purge until observability export embeds base64 for synced rows.
    this.lastMediaPurgeSkipped = countReposicionMediaPurgeSkipped(pending, syncedIds, storedMedia);
    return syncedIds;
  }

  private backedUpExportMediaId(mediaId: string | null | undefined, base64: string | null | undefined): string | null {
    const id = mediaId?.trim();
    if (!id || typeof base64 !== 'string' || !base64.trim()) return null;
    return id;
  }

  private collectBackedUpExportMediaIds(
    raw: ReposicionExportRow[],
    payload: PendingReposicionPush[],
  ): { purged: string[]; retained: number } {
    const payloadById = new Map(payload.map((row) => [row.publicId, row]));
    const purged = new Set<string>();
    const retainedIds = new Set<string>();
    const consider = (synced: boolean, mediaId: string | null | undefined, base64: string | null | undefined) => {
      const id = this.backedUpExportMediaId(mediaId, base64);
      if (!id) return;
      if (synced) purged.add(id);
      else retainedIds.add(id);
    };
    for (const row of raw) {
      const enriched = payloadById.get(row.publicId);
      if (!enriched) continue;
      const synced = row.syncStatus === 'synced';
      consider(synced, row.firmaMediaId, enriched.firmaBase64);
      consider(synced, row.fotoMediaId, enriched.fotoBase64);
    }
    return { purged: [...purged].filter((id) => !retainedIds.has(id)), retained: retainedIds.size };
  }

  private async enrichPendingPush(pending: PendingReposicionRow[]): Promise<PendingReposicionPush[]> {
    // ponytail: serial media→base64; parallel OOM Safari/iOS.
    const actor = this.resolvePushActor ? await this.resolvePushActor() : null;
    const usuarioPublicId = actor?.usuarioPublicId?.trim() ?? '';
    const out: PendingReposicionPush[] = [];
    for (const row of pending) {
      const { firmaMediaId, fotoMediaId, parentescoPublicId: _p, documentoAcudiente, nombreRecibe, ...rest } = row;
      out.push({
        publicId: rest.publicId,
        usuarioPublicId,
        comunaPublicId: rest.comunaPublicId,
        direccion: rest.direccion,
        fecha: rest.fecha,
        beneficiarioPublicId: rest.beneficiarioPublicId,
        beneficiarioDocumento: rest.beneficiarioDocumento,
        beneficiarioNombre: rest.beneficiarioNombre,
        productos: rest.productos,
        medioReportePublicId: rest.medioReportePublicId,
        motivoReposicionPublicId: rest.motivoReposicionPublicId,
        recibeOtraPersona: rest.recibeOtraPersona,
        nombreRecibe: nombreRecibe ?? '',
        documentoRecibe: documentoAcudiente ?? '',
        observaciones: rest.observaciones,
        firmaBase64: await mediaBase64FromId((id) => this.media.get(id), (b) => this.media.toBase64(b), firmaMediaId),
        fotoBase64: await mediaBase64FromId((id) => this.media.get(id), (b) => this.media.toBase64(b), fotoMediaId),
      });
    }
    return out;
  }
}
